import type { QuoteHighlightPhase } from './quoteNavigationHighlight';
import { BodyCopy } from 'Components/shared/BodyCopy';
import styled, { css, keyframes } from 'styled-components';
import { LARGE_EMOJI_FONT_SIZE_PX } from 'Utils/emojiUtils';
import { theme } from '~/styles/theme/Theme';
import {
  QUOTE_NAVIGATION_HIGHLIGHT_COLOR,
  QUOTE_NAVIGATION_HIGHLIGHT_MS,
} from './quoteNavigationHighlight';

const fadeQuoteHighlight = (restColor: string) => keyframes`
  from {
    background-color: ${QUOTE_NAVIGATION_HIGHLIGHT_COLOR};
  }
  to {
    background-color: ${restColor};
  }
`;

const fadeOwnQuoteHighlight = fadeQuoteHighlight(theme.colors.lightGray);
const fadeOtherQuoteHighlight = fadeQuoteHighlight(theme.colors.base);

const ParentBox = styled.div<{
  $isOwnMessage: boolean;
  $hasReactions?: boolean;
  $isPinnedView?: boolean;
}>`
  width: fit-content;
  max-width: 64%;
  ${({ $isOwnMessage }) => $isOwnMessage && 'margin: 0 5px 5px auto;'}
`;

const BoxWithAvatar = styled.div<{ $isOwnMessage: boolean }>`
  display: flex;
  ${({ $isOwnMessage }) =>
    $isOwnMessage &&
    css`
      justify-content: flex-end;
    `}
  margin: 3px 0 7px;
`;

const Box = styled.div<{
  $isOwnMessage: boolean;
  $quoteHighlightPhase?: QuoteHighlightPhase;
}>`
  border-radius: 4px;
  position: relative;
  padding: 5px 10px 5px;
  transition: min-width 0.3s ease-in-out;
  min-width: 230px;

  ::after {
    content: '';
    position: absolute;
    top: 10px;
    transform: rotate(-45deg);
    width: 11px;
    height: 11px;
  }

  ${({ $isOwnMessage }) =>
    $isOwnMessage
      ? css`
          background: ${({ theme }) => theme.colors.lightGray};
          ::after {
            right: -5px;
            background: ${({ theme }) => theme.colors.lightGray};
          }
        `
      : css`
          border: 1px solid ${({ theme }) => theme.colors.border};
          ::after {
            background: ${({ theme }) => theme.colors.base};
            left: -6px;
            border-top: 1px solid ${({ theme }) => theme.colors.border};
            border-left: 1px solid ${({ theme }) => theme.colors.border};
          }
        `}
  ${({ onClick }) => onClick && 'cursor: pointer'}

  ${({ $quoteHighlightPhase }) =>
    $quoteHighlightPhase === 'holding' &&
    css`
      background: ${QUOTE_NAVIGATION_HIGHLIGHT_COLOR};

      &::after {
        background: ${QUOTE_NAVIGATION_HIGHLIGHT_COLOR};
      }
    `}

  ${({ $isOwnMessage, $quoteHighlightPhase }) =>
    $quoteHighlightPhase === 'fading' &&
    css`
      animation: ${$isOwnMessage
          ? fadeOwnQuoteHighlight
          : fadeOtherQuoteHighlight}
        ${QUOTE_NAVIGATION_HIGHLIGHT_MS}ms linear forwards;

      &::after {
        animation: ${$isOwnMessage
            ? fadeOwnQuoteHighlight
            : fadeOtherQuoteHighlight}
          ${QUOTE_NAVIGATION_HIGHLIGHT_MS}ms linear forwards;
      }
    `}
`;

const NameDateContainer = styled.div`
  display: flex;
  align-items: start;
`;

const Date = styled(BodyCopy)`
  white-space: nowrap;
  margin-left: auto;
`;

const EditTag = styled(BodyCopy)`
  margin-left: ${({ theme }) => theme.sizes.xxxsmall};
`;

const Name = styled(BodyCopy)<{ $showThreeDots: boolean }>`
  font-weight: 700;
  margin-bottom: 3px;
  word-break: break-word;
  ${({ $showThreeDots }) =>
    `margin-right: ${$showThreeDots ? '10px' : '40px'};`}
`;

const Message = styled(BodyCopy)<{ $largeEmoji?: boolean }>`
  margin-bottom: 5px;
  word-break: break-word;
  white-space: preserve wrap;
  line-height: 1.4285em;

  ${({ $largeEmoji }) =>
    $largeEmoji &&
    css`
      font-size: ${LARGE_EMOJI_FONT_SIZE_PX}px;
    `}
`;

const AvatarContainer = styled.div`
  align-self: baseline;
  margin-right: 10px;
`;

export const Styled = {
  AvatarContainer,
  Box,
  BoxWithAvatar,
  Date,
  EditTag,
  Message,
  Name,
  NameDateContainer,
  ParentBox,
};
